/**
 * 토큰 확보 (플랫폼 SSO)
 * ---------------------------------------------------------------------------
 * 제품에는 로그인 화면이 없다. 플랫폼에서 로그인하고, 제품은 그 세션으로
 * **자기 몫의 토큰**을 받아 온다.
 *
 * 흐름
 *   1. 주소에 #sso_code 가 있으면 → 교환해서 토큰과 제품 전용 쿠키를 받는다
 *   2. 없으면 → 제품 전용 쿠키로 갱신을 시도한다
 *   3. 그것도 없으면 → 플랫폼의 /sso 로 보낸다 (돌아올 주소를 함께)
 *
 * 왜 플랫폼의 refresh 쿠키를 직접 쓰지 않는가
 *   그 토큰은 1회용으로 회전한다. 콘솔 탭과 제품 탭이 같은 쿠키를 공유하면
 *   나중에 갱신하는 쪽이 이미 쓴 토큰을 내밀게 되고, 플랫폼은 그것을 탈취로
 *   보고 **그 사용자의 세션을 전부 끊는다.** 두 사이트를 같이 열면 반드시
 *   일어나는 일이다. 실제로 그래서 양쪽 모두 로그인이 풀렸다.
 *
 *   그래서 제품은 `wnh_rt_eco` 라는 자기 쿠키를 따로 받는다. 체인이 갈라져
 *   있어 서로를 죽이지 않고, 재사용 탐지는 체인별로 그대로 살아 있다.
 *
 * 액세스 토큰은 메모리에만 둔다. localStorage 에 두면 XSS 한 번에 나간다.
 */

const PLATFORM_API = import.meta.env.VITE_PLATFORM_API_BASE_URL ?? 'http://localhost:3000/api';
const PLATFORM_WEB = import.meta.env.VITE_PLATFORM_WEB_BASE_URL ?? 'http://localhost:5273';
const PRODUCT_CODE = import.meta.env.VITE_PRODUCT_CODE ?? 'eco';

let accessToken: string | null = null;
let expiresAt = 0;
let inflight: Promise<string | null> | null = null;

/** 만료 직전에 미리 갱신한다. 요청 도중에 만료되면 그 요청만 실패한다. */
const RENEW_MARGIN_MS = 30_000;

export function getAccessToken(): string | null {
  return accessToken;
}

export function clearAccessToken(): void {
  accessToken = null;
  expiresAt = 0;
}

function store(body: { accessToken?: string; expiresIn?: number }): string | null {
  if (!body.accessToken) {
    clearAccessToken();
    return null;
  }
  accessToken = body.accessToken;
  expiresAt = Date.now() + (body.expiresIn ?? 900) * 1000;
  return accessToken;
}

/**
 * 주소에 실려 온 1회용 코드를 꺼낸다.
 *
 * 프래그먼트(#)를 쓴다. 쿼리로 넘기면 서버 로그와 Referer 에 코드가 남는다.
 * 꺼낸 뒤에는 주소에서 지운다 — 새로고침하면 이미 쓴 코드로 다시 시도하게 된다.
 */
function takeSsoCode(): string | null {
  const hash = window.location.hash.replace(/^#/, '');
  if (!hash) return null;

  const params = new URLSearchParams(hash);
  const code = params.get('sso_code');
  if (!code) return null;

  params.delete('sso_code');
  const rest = params.toString();
  window.history.replaceState(
    null,
    '',
    `${window.location.pathname}${window.location.search}${rest ? `#${rest}` : ''}`,
  );

  return code;
}

/**
 * 유효한 액세스 토큰을 돌려준다. 없으면 받아 온다.
 * 끝내 못 받으면 null — 호출부가 로그인 화면으로 보낸다.
 */
export async function ensureAccessToken(): Promise<string | null> {
  if (accessToken && Date.now() < expiresAt - RENEW_MARGIN_MS) return accessToken;
  return acquire();
}

/** 401 을 받은 직후처럼 캐시를 믿을 수 없을 때 강제로 다시 받는다. */
export async function forceRefresh(): Promise<string | null> {
  clearAccessToken();
  return acquire();
}

function acquire(): Promise<string | null> {
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      // 1) 플랫폼이 막 건네준 코드가 있으면 그것을 먼저 쓴다.
      const code = takeSsoCode();
      if (code) {
        const res = await fetch(`${PLATFORM_API}/auth/sso/exchange`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ code, productCode: PRODUCT_CODE }),
        });
        if (res.ok) return store(await res.json());
        // 코드가 만료·중복 사용된 경우다. 아래 갱신으로 넘어간다.
      }

      // 2) 제품 전용 쿠키로 갱신.
      const res = await fetch(`${PLATFORM_API}/auth/sso/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ productCode: PRODUCT_CODE }),
      });

      if (res.ok) return store(await res.json());

      clearAccessToken();
      return null;
    } catch {
      // 플랫폼이 꺼져 있거나 네트워크가 끊긴 경우. 로그인 화면으로 보내면
      // 거기서도 같은 이유로 실패하므로 호출부가 안내를 맡는다.
      clearAccessToken();
      return null;
    } finally {
      inflight = null;
    }
  })();

  return inflight;
}

/**
 * 플랫폼으로 보낸다. 로그인이 되어 있으면 바로 코드를 받아 돌아온다.
 *
 * 돌아올 주소에는 프래그먼트를 붙이지 않는다. 지금 주소에 이미 쓴 코드가
 * 남아 있을 수 있고, 그것을 그대로 들고 가면 루프가 된다.
 */
export function goToPlatformLogin(): void {
  const back = `${window.location.origin}${window.location.pathname}${window.location.search}`;
  const url =
    `${PLATFORM_WEB}/sso?product=${encodeURIComponent(PRODUCT_CODE)}` +
    `&next=${encodeURIComponent(back)}`;
  window.location.replace(url);
}

/** 제품에서 나간다. 플랫폼 세션은 그대로 둔다. */
export async function signOut(): Promise<void> {
  clearAccessToken();
  try {
    await fetch(`${PLATFORM_API}/auth/sso/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ productCode: PRODUCT_CODE }),
    });
  } catch {
    // 쿠키를 못 지워도 화면은 넘어가야 한다.
  }
  window.location.replace(PLATFORM_WEB);
}

export const platformWebUrl = PLATFORM_WEB;
